import React from "react";
import { jsonLdScriptProps } from "react-schemaorg";
import { Brand, ItemList, WithContext } from "schema-dts";

import {
  BrandDetail,
  ProductCategory,
  ProductsByCategory,
} from "custom-types/BrandDetail";
import useDomain from "hooks/useDomain";
import useDomainCountryCode from "hooks/useDomainCountryCode";
import generateBrandSchema from "utils/schemas/generateBrandSchema";
import generateItemListSchema from "utils/schemas/generateItemListSchema";

import { SailthruMetaTags } from "components/brands/SailthruMetaTags";
import MetaTags from "components/MetaTags";

const BrandHead = ({
  brand,
  productsByCategory,
  sortedCategories,
}: {
  brand: BrandDetail;
  productsByCategory: ProductsByCategory;
  sortedCategories: ProductCategory[];
}) => {
  const domain = useDomain();
  const domainCountryCode = useDomainCountryCode();

  const pageTitle = `${[brand.name, brand.tagline].filter((s) => !!s).join(": ")} | Leafly`;
  const metaDescription = `Learn about ${brand.name} including who they are, their products, and where you can find them.`;
  const shouldShowAlternates = ["United States", "Canada"].every((country) =>
    brand.availableCountries.includes(country),
  );

  return (
    <>
      <MetaTags
        description={metaDescription}
        image={brand.logo}
        shouldShowAlternates={shouldShowAlternates}
        title={pageTitle}
      >
        <script
          {...jsonLdScriptProps<Brand>(
            generateBrandSchema(brand, domain) as WithContext<Brand>,
          )}
        />
        {sortedCategories &&
          sortedCategories?.length > 0 &&
          sortedCategories.map((category) => (
            <script
              key={category.name}
              {...jsonLdScriptProps<ItemList>(
                generateItemListSchema(
                  brand,
                  category,
                  productsByCategory[category.slug],
                  domainCountryCode,
                  domain,
                ) as WithContext<ItemList>,
              )}
            />
          ))}
      </MetaTags>
      <SailthruMetaTags
        description={metaDescription}
        imageUrl={brand.logo}
        tags={[
          brand.name,
          "brand-page",
          ...brand.availableStates,
          ...brand.availableCountries,
        ]}
        title={pageTitle}
      />
    </>
  );
};

export default BrandHead;
